/*
  Homepage video tutorials (snippets/video-tutorials.liquid).

  Progressive enhancement over plain markup. Without this file every card
  links to its video on YouTube and the product buttons still work.

  - The row: arrows scroll it a card at a time and are shown only while it
    overflows (phones, tablets, or more videos than fit on desktop).
  - Silent previews: one plays at a time, muted. It is the card under the
    mouse or keyboard focus, otherwise the first card mostly on screen.
    A clip is fetched only when its card first becomes that one, because
    Chrome downloads the whole file as soon as it plays. Three at once was
    21 MB. Skipped entirely for reduced motion or Save-Data. Each preview has
    a pause button (WCAG 2.2.2); once a visitor pauses one, it stays paused
    and no other starts by itself.
  - The dialog: a card opens its full video in a native <dialog>, beside the
    steps and every product used. Previous and next move between videos. The
    player is built on open and thrown away on close, which is what stops
    playback, and keeps YouTube off the page until someone asks for a video.

  Buying from the dialog. assets/best-sellers.js posts Add to Bag and fires
  cart:updated once the bag is refreshed. The dialog closes then, so the
  Unicorn cart drawer that opens is not left underneath a modal dialog. A
  failed add leaves the dialog open with the error showing. "Choose shade"
  closes the dialog at once, because the theme quick view would otherwise
  open beneath it.
*/
(() => {
  if (customElements.get('video-tutorials')) return;

  const PLAY_RATIO = 0.6;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VideoTutorials extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;
    #track = null;
    #dialog = null;
    #player = null;
    /** @type {HTMLElement[]} panels of the videos that can be played */
    #panels = [];
    #index = 0;
    #opener = null;
    #restoreFocus = true;
    #addPending = false;
    #bodyPadding = '';
    /** @type {HTMLVideoElement[]} */
    #previews = [];
    /** @type {HTMLVideoElement | null} the preview under the mouse or keyboard focus */
    #hovered = null;
    #previewObserver = null;
    #resizeObserver = null;
    #frame = 0;

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      this.#track = this.querySelector('[data-tutorial-track]');
      this.#dialog = this.querySelector('[data-tutorial-dialog]');
      this.#player = this.querySelector('[data-tutorial-player]');
      this.#panels = Array.from(this.querySelectorAll('[data-tutorial-panel]')).filter(
        (panel) => panel.dataset.embed
      );

      this.#setUpRow(signal);
      this.#setUpPreviews(signal);
      this.#setUpDialog(signal);

      // Theme editor: selecting a video block brings its card into the row's view.
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!(event.target instanceof Element)) return;
          event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#previewObserver?.disconnect();
      this.#resizeObserver?.disconnect();
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;

      // The theme editor swaps the section out from under an open dialog; drop
      // the player and the scroll lock rather than leave them behind.
      this.#player?.replaceChildren();
      if (this.#dialog?.open) this.#dialog.close();
      this.#unlockScroll();
    }

    /* ---------------------------------------------------------------- Row */

    /** @param {AbortSignal} signal */
    #setUpRow(signal) {
      const track = this.#track;
      const controls = this.querySelector('[data-tutorial-controls]');
      const arrows = Array.from(this.querySelectorAll('[data-tutorial-scroll]'));
      if (!track) return;

      const update = () => {
        this.#frame = 0;
        const max = track.scrollWidth - track.clientWidth;
        const overflowing = max > 1;

        track.classList.toggle('is-overflowing', overflowing);
        if (controls) controls.hidden = !overflowing;

        for (const arrow of arrows) {
          const atEnd =
            Number(arrow.dataset.tutorialScroll) < 0 ? track.scrollLeft <= 1 : track.scrollLeft >= max - 1;
          // A focused button that turns disabled drops focus to the page, so
          // hand it to the other arrow first.
          if (atEnd && document.activeElement === arrow) {
            arrows.find((other) => other !== arrow)?.focus();
          }
          arrow.disabled = atEnd;
        }
      };

      const schedule = () => {
        if (!this.#frame) this.#frame = requestAnimationFrame(update);
      };

      track.addEventListener('scroll', schedule, { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(schedule);
      this.#resizeObserver.observe(track);
      update();

      for (const arrow of arrows) {
        arrow.addEventListener(
          'click',
          () => {
            const card = track.querySelector('.tutorial-card');
            const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
            const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
            track.scrollBy({
              left: step * Number(arrow.dataset.tutorialScroll),
              behavior: reducedMotion.matches ? 'auto' : 'smooth',
            });
          },
          { signal }
        );
      }
    }

    /* ----------------------------------------------------------- Previews */

    /** @param {AbortSignal} signal */
    #setUpPreviews(signal) {
      this.#previews = Array.from(this.querySelectorAll('[data-tutorial-preview]'));
      if (!this.#previews.length || !('IntersectionObserver' in window)) return;
      if (reducedMotion.matches || navigator.connection?.saveData) return;

      this.#previewObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = /** @type {HTMLVideoElement} */ (entry.target);
            video.dataset.inView = String(entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO);
          }
          this.#syncPreviews();
        },
        { threshold: [0, PLAY_RATIO, 1] }
      );

      for (const video of this.#previews) {
        const media = video.closest('.tutorial-card__media');
        const button = media?.querySelector('[data-tutorial-pause]');

        // Shown only once frames are on screen: until then there is nothing
        // to pause, and with previews off it never appears at all.
        video.addEventListener(
          'playing',
          () => {
            video.classList.add('is-playing');
            if (button) button.hidden = false;
          },
          { signal }
        );

        // Handed over to another card: back to the thumbnail. A preview the
        // visitor paused keeps its frame.
        video.addEventListener(
          'pause',
          () => {
            if (video.dataset.userPaused !== 'true') video.classList.remove('is-playing');
          },
          { signal }
        );

        const take = () => {
          this.#hovered = video;
          this.#syncPreviews();
        };
        const release = () => {
          if (this.#hovered !== video) return;
          this.#hovered = null;
          this.#syncPreviews();
        };
        media?.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && take(), { signal });
        media?.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && release(), { signal });
        media?.addEventListener('focusin', take, { signal });
        media?.addEventListener(
          'focusout',
          (event) => {
            if (!media.contains(/** @type {Node | null} */ (event.relatedTarget))) release();
          },
          { signal }
        );

        button?.addEventListener(
          'click',
          () => {
            const paused = video.dataset.userPaused !== 'true';
            video.dataset.userPaused = String(paused);
            button.classList.toggle('is-paused', paused);
            button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
            this.#syncPreviews();
          },
          { signal }
        );

        this.#previewObserver.observe(video);
      }
    }

    /**
     * The one preview that should be playing, if any: the card under the mouse
     * or keyboard focus, else the first card mostly on screen. Once a visitor
     * has paused a preview, only hovering or focusing a card starts one again.
     * @returns {HTMLVideoElement | null}
     */
    #activePreview() {
      if (this.#dialog?.open) return null;

      const ready = this.#previews.filter(
        (video) => video.dataset.inView === 'true' && video.dataset.userPaused !== 'true'
      );
      if (this.#hovered && ready.includes(this.#hovered)) return this.#hovered;
      if (this.#previews.some((video) => video.dataset.userPaused === 'true')) return null;
      return ready[0] || null;
    }

    #syncPreviews() {
      if (!this.#previewObserver) return;

      const active = this.#activePreview();
      for (const video of this.#previews) {
        if (video !== active) {
          if (!video.paused) video.pause();
          continue;
        }
        if (!video.getAttribute('src')) video.src = video.dataset.src;
        // Autoplay can still be refused (iOS Low Power Mode); the thumbnail
        // simply stays, which is the right fallback.
        video.play().catch(() => {});
      }
    }

    /* ------------------------------------------------------------- Dialog */

    /** @param {AbortSignal} signal */
    #setUpDialog(signal) {
      const dialog = this.#dialog;
      if (!dialog || !this.#player || typeof dialog.showModal !== 'function' || !this.#panels.length) return;

      this.addEventListener(
        'click',
        (event) => {
          const opener = event.target instanceof Element ? event.target.closest('[data-tutorial-open]') : null;
          if (!opener) return;
          // A modified click on the card's link asks for a new tab, which the
          // real YouTube link already gives.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

          event.preventDefault();
          this.#open(opener.dataset.tutorialOpen, opener);
        },
        { signal }
      );

      // One video has nowhere to step to, and "Video 1 of 1" says nothing.
      const nav = dialog.querySelector('.tutorial-modal__nav');
      if (nav) nav.hidden = this.#panels.length < 2;

      for (const button of dialog.querySelectorAll('[data-tutorial-step]')) {
        button.addEventListener('click', () => this.#show(this.#index + Number(button.dataset.tutorialStep)), {
          signal,
        });
      }

      dialog.querySelector('[data-tutorial-close]')?.addEventListener('click', () => this.#close(), { signal });

      dialog.addEventListener(
        'click',
        (event) => {
          // The dialog's content fills it edge to edge, so a click on the
          // dialog element itself can only have landed on the backdrop.
          if (event.target === dialog) {
            this.#close();
            return;
          }
          if (event.target instanceof Element && event.target.closest('[data-quick-view-trigger]')) {
            this.#close({ restoreFocus: false });
          }
        },
        { signal }
      );

      dialog.addEventListener('close', () => this.#onClose(), { signal });

      dialog.addEventListener(
        'submit',
        () => {
          this.#addPending = true;
        },
        { signal }
      );

      document.addEventListener(
        'cart:updated',
        () => {
          if (!this.#addPending || !dialog.open) return;
          this.#addPending = false;
          this.#close({ restoreFocus: false });
        },
        { signal }
      );
    }

    /**
     * @param {string | undefined} cardIndex - the card's data-tutorial-open
     * @param {HTMLElement} opener
     */
    #open(cardIndex, opener) {
      const position = this.#panels.findIndex((panel) => panel.dataset.tutorialPanel === cardIndex);
      if (position < 0) return;

      this.#opener = opener;
      this.#restoreFocus = true;

      if (!this.#dialog.open) {
        this.#lockScroll();
        this.#dialog.showModal();
      }
      this.#show(position);
      this.#syncPreviews();
      this.#dialog.querySelector('[data-tutorial-close]')?.focus();
    }

    /** @param {number} position - wraps around at either end */
    #show(position) {
      const total = this.#panels.length;
      this.#index = (position + total) % total;
      const panel = this.#panels[this.#index];

      for (const other of this.#panels) other.hidden = other !== panel;

      const title = panel.querySelector('.tutorial-panel__title');
      if (title?.id) {
        this.#dialog.setAttribute('aria-labelledby', title.id);
      } else {
        this.#dialog.removeAttribute('aria-labelledby');
        this.#dialog.setAttribute('aria-label', panel.dataset.title || '');
      }

      const count = this.#dialog.querySelector('[data-tutorial-count]');
      if (count) {
        count.textContent = (this.#dialog.dataset.countTemplate || '')
          .replace('[current]', String(this.#index + 1))
          .replace('[total]', String(total));
      }

      this.#player.replaceChildren(this.#buildPlayer(panel.dataset.embed, panel.dataset.title));
      this.#dialog.querySelector('.tutorial-modal__body')?.scrollTo(0, 0);
    }

    /** @param {{ restoreFocus?: boolean }} [options] */
    #close({ restoreFocus = true } = {}) {
      this.#restoreFocus = restoreFocus;
      if (this.#dialog?.open) this.#dialog.close();
    }

    #onClose() {
      // Removing the iframe is what stops the sound.
      this.#player.replaceChildren();
      this.#addPending = false;
      this.#unlockScroll();
      this.#syncPreviews();

      if (this.#restoreFocus && this.#opener?.isConnected) {
        this.#opener.focus({ preventScroll: true });
      }
      this.#opener = null;
    }

    /**
     * @param {string} embed - player URL from Liquid, without a query string
     * @param {string} [title]
     * @returns {HTMLIFrameElement}
     */
    #buildPlayer(embed, title) {
      const url = new URL(embed);
      url.searchParams.set('autoplay', '1');
      if (url.hostname.includes('youtube')) {
        url.searchParams.set('rel', '0');
        url.searchParams.set('playsinline', '1');
      }

      const iframe = document.createElement('iframe');
      iframe.src = url.toString();
      iframe.title = title || '';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      return iframe;
    }

    /* The root stops scrolling and keeps its offset, so the page is exactly
       where it was on close. The scrollbar's width goes to the body as padding
       while it is gone, so a desktop page does not shift sideways. */
    #lockScroll() {
      const root = document.documentElement;
      if (root.classList.contains('has-tutorial-modal')) return;

      const gap = window.innerWidth - root.clientWidth;
      this.#bodyPadding = document.body.style.paddingRight;
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
      root.classList.add('has-tutorial-modal');
    }

    #unlockScroll() {
      const root = document.documentElement;
      if (!root.classList.contains('has-tutorial-modal')) return;

      root.classList.remove('has-tutorial-modal');
      document.body.style.paddingRight = this.#bodyPadding;
    }
  }

  customElements.define('video-tutorials', VideoTutorials);
})();
